import events from "add-event-listener";
import * as React from "react";
import { AllIconNames, Button, Popup, RenderLayer, Sticky } from "react-ui";

import { isLengthGreaterThenOrEqual, noop, runAsyncAction } from "../../../../../Common/TypingUtils";
import { CLEAR_PANEL_CLICK, FEEDBACK_PANEL_CLICK, FUF_PANEL_CLICK, OPEN_TEMPLATES_CLICK } from "../../metricsAction";
import { trackAction } from "../../Internals/Metrics/MetricsProvider";
import { emptyRef } from "../../TypingUtils";
import { ActionButtonContainer } from "../ActionButton/ActionButtonContainer";
import { SendActionButtonContainer } from "../ActionButton/SendActionButtonContainer";
import { ActionItem } from "../ActionItem/ActionItem";
import { FeedbackButton } from "../FeedbackButton/FeedbackButton";
import { ScrollToProvider } from "../../Internals/Contexts/ScrollToContext";
import { ButtonSettingsTypeEnum } from "../../../../../Common/Enums/ButtonSettingsTypeEnum";
import { TemplatesModalContainer } from "../Panel/TemplatesModal/TemplatesModalContainer";
import { ModalHeader } from "../ModalHeader/ModalHeader";

import {
    ActionButtonsContainer,
    FooterAdditionalText,
    Gap,
    List,
    Panel,
    PopupAction,
    PopupItem,
    PopupList,
    SimpleFormContainer,
    SimpleFormContent,
    SimpleFormRoot,
} from "./SimpleForm.styles";

type ActionType = "feedback" | "remove" | "fuf" | "templates";

const actions: ActionType[] = ["feedback", "remove", "fuf", "templates"];

export interface customButtonOptions {
    visible?: boolean;
    title?: string;
}

export interface SimpleFormProps {
    children: React.ReactNode;
    disabledSending: boolean;
    maxWidth: number;
    cssWidth?: string;
    buttonSettingsType?: ButtonSettingsTypeEnum;
    minWidth: number;
    padding?: string | number;
    showFormContainer: boolean;
    feedbackEnabled: boolean;
    onSaveFuf: () => Promise<void>;
    onRunFeedback: () => void;
    onFormClear: () => Promise<void>;
    onCloseForm: () => Promise<void>;
    onSendToSign: () => Promise<void>;
    onSaveForm: () => Promise<void>;
    isReadOnly?: boolean;
    validationBeforeSend?: boolean;
    sendToSignButtonOptions?: customButtonOptions;
    signAndSendButtonOptions?: customButtonOptions;
    isEnableTemplates?: boolean;
}

interface SimpleFormState {
    isPopupOpen: boolean;
    screenWidth: number;
    isTemplatesModalOpen: boolean;
    modalHeader: React.ReactNode;
    formSections: React.ReactNode;
}

type ActionDescription =
    | {
          condition: () => boolean | undefined;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          container: React.ComponentType<any>;
          id: string;
          onClick: () => void;
          icon?: AllIconNames | "";
          text?: string;
      }
    | {
          condition: () => boolean | undefined;
          id: string;
          onClick: () => void;
          icon: AllIconNames | "";
          text: string;
      };

function renderAction(actionInfo: ActionDescription): JSX.Element {
    if ("container" in actionInfo) {
        return React.createElement(actionInfo.container, {
            icon: actionInfo.icon,
            key: actionInfo.id,
            onClick: actionInfo.onClick,
            text: actionInfo.text,
            place: "footer",
        });
    }
    return (
        <ActionItem
            // @ts-ignore нет иконки
            icon={actionInfo.icon}
            key={actionInfo.id}
            id={actionInfo.id}
            onClick={actionInfo.onClick}
            text={actionInfo.text}
            place={"footer"}
        />
    );
}

export class SimpleForm extends React.Component<SimpleFormProps, SimpleFormState> {
    public static defaultProps = {
        maxWidth: 774,
        minWidth: 600,
    };

    public state: SimpleFormState = {
        isPopupOpen: false,
        screenWidth: 0,
        isTemplatesModalOpen: false,
        modalHeader: null,
        formSections: null,
    };
    private readonly anchor = React.createRef<HTMLDivElement & HTMLSpanElement & HTMLLIElement>();

    private readonly actionsDescription: { [actionType in ActionType]: ActionDescription } = {
        feedback: {
            condition: () => this.props.feedbackEnabled,
            container: FeedbackButton,
            id: "feedback",
            onClick: () => this.handleOnClickFeedback(),
        },
        fuf: {
            condition: () => true,
            icon: "FileFormatXmlIcon",
            id: "fuf",
            text: "Скачать",
            onClick: () => this.handleOnClickFufSave(),
        },
        remove: {
            condition: () => true,
            icon: "XIcon",
            id: "remove",
            text: "Очистить форму",
            onClick: () => this.handleOnClickClear(),
        },
        templates: {
            condition: () => this.props.isEnableTemplates,
            icon: "DocsIcon",
            id: "templates",
            text: "Шаблоны",
            onClick: () => this.handleOnOpenTemplatesModal(),
        },
    };

    private panelStop: null | HTMLElement = emptyRef;

    public componentDidMount(): void {
        events.addEventListener(window, "resize", this.handleResize);
        this.parseChildren();
    }

    public componentWillUnmount(): void {
        events.removeEventListener(window, "resize", this.handleResize);
    }

    private parseChildren(): void {
        const [firstChild, ...restChildren] = React.Children.toArray(this.props.children);

        if (firstChild && ModalHeader.isModalHeaderComponent(firstChild)) {
            this.setState({
                modalHeader: firstChild,
                formSections: restChildren,
            });
            return;
        }

        this.setState({
            formSections: this.props.children,
        });
    }

    private getActionButtonText(): string {
        switch (this.props.buttonSettingsType) {
            case ButtonSettingsTypeEnum.Revise:
                return "Перейти к отправке";
            case ButtonSettingsTypeEnum.DigitalWarrant:
                return this.props.signAndSendButtonOptions?.title ?? "Подписать и отправить";
            default:
                return "Проверить и отправить";
        }
    }

    private renderFooterText(): JSX.Element | null {
        switch (this.props.buttonSettingsType) {
            case ButtonSettingsTypeEnum.DigitalWarrant:
                return (
                    <FooterAdditionalText>
                        Для подписи потребуется сертификат организации, выданный на руководителя.
                    </FooterAdditionalText>
                );
            default:
                return null;
        }
    }

    private getActionButtons(): JSX.Element {
        const sendButton = (
            <Gap>
                <SendActionButtonContainer
                    data-tid={`SimpleForm-Send`}
                    validationBeforeSend={this.props.validationBeforeSend}
                    disabled={this.props.disabledSending}
                    text={this.getActionButtonText()}
                    size="large"
                />
            </Gap>
        );
        let cancelButton = null;
        switch (this.props.buttonSettingsType) {
            case ButtonSettingsTypeEnum.Revise:
                break;
            case ButtonSettingsTypeEnum.DigitalWarrant:
                break;
            default:
                cancelButton = (
                    <Gap>
                        <ActionButtonContainer type="save" use="default" size="large" />
                    </Gap>
                );
        }

        const handleSendToClientForSign = () => {
            runAsyncAction(this.props.onSendToSign);
        };

        let sendToSignButton = null;
        if (this.props.sendToSignButtonOptions?.visible) {
            sendToSignButton = (
                <Gap>
                    <Button onClick={handleSendToClientForSign} size="large">
                        {this.props.sendToSignButtonOptions?.title ?? "Запросить подпись"}
                    </Button>
                </Gap>
            );
        }

        return (
            <div>
                {sendButton}
                {cancelButton}
                {sendToSignButton}
            </div>
        );
    }

    public render(): JSX.Element {
        const width = this.calculateWidth();
        if (!this.props.showFormContainer) {
            return <div>{this.renderFormSections()}</div>;
        }

        return (
            <SimpleFormContainer id="LoadedFormComponent" minWidth={this.props.minWidth}>
                <ScrollToProvider value={noop}>
                    {this.state.isTemplatesModalOpen && (
                        <TemplatesModalContainer
                            onClose={this.handleOnCloseTemplatesModal}
                            onSaveForm={this.props.onSaveForm}
                        />
                    )}
                    <div ref={this.refPanelStop} />
                    <SimpleFormRoot width={width}>
                        {this.renderModalHeader()}
                        <SimpleFormContent customButtons={this.props.buttonSettingsType} padding={this.props.padding}>
                            {this.renderFormSections()}
                        </SimpleFormContent>
                        <Sticky getStop={this.getStop} side="bottom">
                            {fixed => (
                                <Panel fixed={fixed} isCustomButtons={!!this.props.buttonSettingsType}>
                                    {this.renderFooterText()}
                                    <ActionButtonsContainer>
                                        {this.getActionButtons()}
                                        {this.renderActions()}
                                    </ActionButtonsContainer>
                                </Panel>
                            )}
                        </Sticky>
                    </SimpleFormRoot>
                </ScrollToProvider>
            </SimpleFormContainer>
        );
    }

    private renderModalHeader(): React.ReactNode {
        return this.state.modalHeader;
    }

    private renderFormSections(): React.ReactNode {
        return this.state.formSections;
    }

    private readonly getStop = (): null | HTMLElement => this.panelStop;

    private readonly calculateWidth = (): number | string => {
        if (this.props.cssWidth != undefined) {
            return this.props.cssWidth;
        }
        let width = Math.min(document.body.clientWidth, this.props.maxWidth);
        width = Math.max(width, this.props.minWidth);
        return width;
    };

    private readonly refPanelStop = (el: null | HTMLElement) => {
        if (el) {
            this.panelStop = el;
        }
    };

    private readonly handleResize = () => {
        if (document.body.offsetWidth !== this.state.screenWidth) {
            if (this.props.showFormContainer) {
                this.setState({ screenWidth: document.body.offsetWidth });
            }
        }
    };

    @trackAction(FUF_PANEL_CLICK)
    private readonly handleOnClickFufSave = async () => {
        await this.props.onSaveFuf();
    };

    @trackAction(CLEAR_PANEL_CLICK)
    private readonly handleOnClickClear = async (): Promise<void> => {
        await this.props.onFormClear();
    };

    @trackAction(FEEDBACK_PANEL_CLICK)
    private readonly handleOnClickFeedback = () => {
        this.props.onRunFeedback();
    };

    private readonly handleOnClickMoreActions = () => {
        this.setState({ isPopupOpen: true });
    };

    private readonly handleOnClickOutsidePopup = () => {
        this.setState({ isPopupOpen: false });
    };

    @trackAction(OPEN_TEMPLATES_CLICK)
    private readonly handleOnOpenTemplatesModal = () => {
        this.setState({ isTemplatesModalOpen: true });
    };

    private readonly handleOnCloseTemplatesModal = () => {
        this.setState({ isTemplatesModalOpen: false });
    };

    private renderOtherActions(actionInfos: ActionDescription[]): JSX.Element {
        const renderedActionInfos = actionInfos.map(actionInfo => (
            <PopupItem key={actionInfo.id}>
                <PopupAction onClick={actionInfo.onClick} data-tid={`panel-action-${actionInfo.id}`}>
                    {actionInfo.text}
                </PopupAction>
            </PopupItem>
        ));

        return (
            <ActionItem
                icon="UiMenuDots3VIcon"
                place="footer"
                ref={this.anchor}
                text="Ещё действия"
                data-tid="more-actions"
                onClick={this.handleOnClickMoreActions}>
                {this.anchor.current && (
                    <Popup
                        hasPin
                        hasShadow
                        anchorElement={this.anchor.current}
                        margin={8}
                        opened={this.state.isPopupOpen}
                        data-tid="MoreActionsPopup"
                        pinOffset={15}
                        popupOffset={-3}
                        positions={["top left"]}>
                        <PopupList>{renderedActionInfos}</PopupList>
                    </Popup>
                )}
            </ActionItem>
        );
    }

    private renderActions(): JSX.Element | null {
        const displayedActions = actions
            .filter(action => !(this.props.isReadOnly && action === "remove"))
            .filter(action => this.actionsDescription[action].condition())
            .map(action => this.actionsDescription[action]);

        switch (this.props.buttonSettingsType) {
            case ButtonSettingsTypeEnum.Revise:
                return (
                    <List>
                        {renderAction(this.actionsDescription["fuf"])}
                        {renderAction(this.actionsDescription["feedback"])}
                        {this.props.isEnableTemplates && renderAction(this.actionsDescription["templates"])}
                    </List>
                );
            case ButtonSettingsTypeEnum.DigitalWarrant:
                return null;
            default:
                if (isLengthGreaterThenOrEqual(displayedActions, 3)) {
                    const [firstAction, ...otherActions] = displayedActions;
                    return (
                        <RenderLayer
                            active={this.state.isPopupOpen}
                            onClickOutside={this.handleOnClickOutsidePopup}
                            onFocusOutside={this.handleOnClickOutsidePopup}>
                            <List>
                                {renderAction(firstAction)}
                                {this.renderOtherActions(otherActions)}
                            </List>
                        </RenderLayer>
                    );
                }
                return <List>{displayedActions.map(renderAction)}</List>;
        }
    }
}
